/** postMessage protocol between the host DemoPlayer and the demo iframe.
 *  Messages are tagged with a `source` string; incoming ones are validated by
 *  shape + tag (custom-scheme webviews can report an opaque origin, so an
 *  origin-only check would break Tauri/Capacitor — the payloads are inert
 *  status/control signals, nothing sensitive rides this channel). */

export const DEMO_MSG_SOURCE = "yc-demo";
export const DEMO_HOST_MSG_SOURCE = "yc-demo-host";

export type DemoStatusMsg = { source: typeof DEMO_MSG_SOURCE; scriptId: string } & (
  | { type: "ready" }
  | { type: "step"; stepIndex: number; total: number }
  | { type: "done" }
  | { type: "error"; message: string }
);

export type DemoHostMsg = { source: typeof DEMO_HOST_MSG_SOURCE } & (
  | { type: "replay" }
  | { type: "setPaused"; paused: boolean }
  | { type: "setTheme"; preset?: string; mode?: "light" | "dark" }
);

export function isDemoStatusMsg(data: unknown): data is DemoStatusMsg {
  return isTagged(data, DEMO_MSG_SOURCE);
}

export function isDemoHostMsg(data: unknown): data is DemoHostMsg {
  return isTagged(data, DEMO_HOST_MSG_SOURCE);
}

function isTagged(data: unknown, source: string): boolean {
  return (
    typeof data === "object" &&
    data !== null &&
    (data as { source?: unknown }).source === source &&
    typeof (data as { type?: unknown }).type === "string"
  );
}

/** Latest status, mirrored for the headless checker (which owns the top window
 *  and gets no postMessage). */
declare global {
  interface Window {
    __demoStatus?: { type: DemoStatusMsg["type"]; message?: string };
  }
}

/** Report status to the embedding host (if any) and the window mirror. */
export function postDemoStatus(msg: DemoStatusMsg): void {
  window.__demoStatus = { type: msg.type, ...(msg.type === "error" ? { message: msg.message } : {}) };
  if (window.parent !== window) window.parent.postMessage(msg, "*");
}
